import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { ArrowLeft, Edit, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { mutate as swrMutate } from 'swr';

import { RevenueAccountFormModal } from '@apps/web/components/admin/revenue/revenue_account_form_modal';
import {
    formatCurrencyFromCents,
    formatDate,
    isRevenueSwrKey,
    REVENUE_ACCOUNT_STATUS_OPTIONS,
} from '@apps/web/components/admin/revenue/revenue_utils';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { Spinner } from '@apps/web/components/ui/spinner';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { useAdminRevenueAccount } from '@apps/web/swr_data_fetching/data_fetching_hooks';

export const Route = createFileRoute('/_app/admin/revenue/$account-id')({
    head: () => ({
        meta: [
            {
                title: 'Revenue Account | Phoebe Admin',
            },
        ],
    }),
    component: RevenueAccountDetailPage,
});

function RevenueAccountDetailPage() {
    const { 'account-id': accountId } = Route.useParams();
    const router = useRouter();
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const {
        data: account,
        isLoading,
        mutate: mutateAccount,
    } = useAdminRevenueAccount(accountId);

    async function handleDelete() {
        if (!account) {
            return;
        }
        const confirmed = window.confirm(
            `Delete ${account.name}? This cannot be undone.`,
        );
        if (!confirmed) {
            return;
        }

        setIsDeleting(true);
        try {
            await getApiClient().delete({
                url: '/admin/revenue/accounts/{account_id}',
                pathParams: { account_id: accountId },
            });
            await swrMutate(isRevenueSwrKey);
            toast.success('Revenue account deleted', {
                persistNotification: false,
            });
            router.navigate({ to: '/admin/revenue' });
        } catch {
            toast.error('Failed to delete revenue account', {
                persistNotification: false,
            });
            setIsDeleting(false);
        }
    }

    if (isLoading) {
        return (
            <div className="text-muted-foreground flex h-40 items-center justify-center gap-2 text-sm">
                <Spinner className="h-4 w-4" />
                <span>Loading revenue account…</span>
            </div>
        );
    }

    if (!account) {
        return (
            <div className="text-muted-foreground flex h-40 items-center justify-center">
                Revenue account not found
            </div>
        );
    }

    const pendingArr = account.pending_arr_cents;

    return (
        <div className="h-full overflow-y-auto">
            <div className="max-w-6xl p-6">
                <div className="mb-6">
                    <Link
                        to="/admin/revenue"
                        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 text-sm"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Revenue
                    </Link>
                </div>
                <div className="space-y-6">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                            <div className="mb-2 flex items-center gap-2">
                                <h1 className="text-2xl font-semibold">
                                    {account.organization_internal_name?.trim() &&
                                    account.organization_internal_name.trim() !==
                                        account.organization_name
                                        ? `${account.name} - ${account.organization_internal_name.trim()}`
                                        : account.name}
                                </h1>
                                <Badge
                                    variant={
                                        account.status === 'active'
                                            ? 'success'
                                            : account.status === 'churned'
                                              ? 'destructive'
                                              : 'secondary'
                                    }
                                >
                                    {
                                        REVENUE_ACCOUNT_STATUS_OPTIONS.find(
                                            (option) =>
                                                option.value === account.status,
                                        )?.label
                                    }
                                </Badge>
                            </div>
                            <p className="text-muted-foreground text-sm">
                                Customer account:{' '}
                                {account.customer_account_name}
                            </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Button
                                variant="outline"
                                onClick={() => setIsEditOpen(true)}
                                className="gap-2"
                            >
                                <Edit className="h-4 w-4" />
                                Edit Account
                            </Button>
                            <Button
                                variant="outline"
                                onClick={handleDelete}
                                disabled={isDeleting}
                                className="gap-2 text-red-600 hover:text-red-700"
                            >
                                <Trash2 className="h-4 w-4" />
                                {isDeleting ? 'Deleting…' : 'Delete'}
                            </Button>
                        </div>
                    </div>
                    <div className="grid gap-6 lg:grid-cols-3">
                        <Card>
                            <CardHeader>
                                <CardTitle>Revenue</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3 text-sm">
                                <KeyValueRow
                                    label="Contracted ARR"
                                    value={formatCurrencyFromCents(
                                        account.contracted_arr_cents,
                                    )}
                                />
                                <KeyValueRow
                                    label="Live ARR"
                                    value={formatCurrencyFromCents(
                                        account.live_arr_cents,
                                    )}
                                />
                                <KeyValueRow
                                    label="Pending"
                                    value={formatCurrencyFromCents(pendingArr)}
                                />
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader>
                                <CardTitle>Dates</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3 text-sm">
                                <KeyValueRow
                                    label="Close Date"
                                    value={formatDate(account.close_date)}
                                />
                                <KeyValueRow
                                    label="Go-Live Date"
                                    value={formatDate(account.go_live_date)}
                                />
                                <KeyValueRow
                                    label="Churned Date"
                                    value={formatDate(account.churned_date)}
                                />
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader>
                                <CardTitle>External Links</CardTitle>
                                <CardDescription>
                                    Optional sync identifiers
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-3 text-sm">
                                <KeyValueRow
                                    label="Attio Company URL"
                                    value={
                                        account.attio_company_url || 'Not set'
                                    }
                                />
                                <KeyValueRow
                                    label="Notion Customer Account URL"
                                    value={account.attio_deal_url || 'Not set'}
                                />
                                <KeyValueRow
                                    label="Stripe Customer"
                                    value={
                                        account.stripe_customer_id || 'Not set'
                                    }
                                />
                            </CardContent>
                        </Card>
                    </div>
                    {account.notes ? (
                        <Card>
                            <CardHeader>
                                <CardTitle>Notes</CardTitle>
                            </CardHeader>
                            <CardContent className="text-sm leading-6">
                                {account.notes}
                            </CardContent>
                        </Card>
                    ) : null}
                </div>
                <RevenueAccountFormModal
                    isOpen={isEditOpen}
                    onClose={() => setIsEditOpen(false)}
                    mode="edit"
                    accountId={accountId}
                    initialData={{
                        organization_id: account.organization_id || '',
                        status: account.status,
                        contracted_arr_dollars:
                            account.contracted_arr_cents / 100,
                        pending_arr_dollars: account.pending_arr_cents / 100,
                        live_arr_dollars: account.live_arr_cents / 100,
                        subscription_rate_dollars:
                            account.subscription_rate_cents / 100,
                        close_date: account.close_date || '',
                        go_live_date: account.go_live_date || '',
                        churned_date: account.churned_date || '',
                        attio_company_url: account.attio_company_url || '',
                        attio_deal_url: account.attio_deal_url || '',
                        stripe_customer_id: account.stripe_customer_id || '',
                        notes: account.notes || '',
                    }}
                    onSaveSuccess={(updatedAccount) =>
                        mutateAccount(updatedAccount, {
                            revalidate: false,
                        })
                    }
                />
            </div>
        </div>
    );
}

function KeyValueRow({
    label,
    value,
}: {
    readonly label: string;
    readonly value: string;
}) {
    return (
        <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">{label}</span>
            <span className="text-right font-medium">{value}</span>
        </div>
    );
}
